import { ReactElement, useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient, type UseQueryResult, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Coins,
  Search,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Kbd } from '@/components/ui/kbd'
import { Input } from '@/components/ui/input'

import { ScrollReveal } from '@/shared/components/ui'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { useIntegrationsStatus } from '@/features/settings/hooks/useIntegrationsStatus'
import type { IntegrationStatus } from '@/features/settings/schemas/integrationStatusSchema'
import type { OrgSettingsResponse } from '@/features/settings/api/orgSettingsService'
import { getBaselinkerCurrency, setBaselinkerCurrency } from '@/features/settings/api/baselinkerCurrencyService'
import { getOrgSettings } from '@/features/settings/api/orgSettingsService'
import { BaselinkerCredentialsPopover } from '@/features/settings/components/BaselinkerCredentialsPopover'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import {
  useBaselinkerInventoriesQuery,
  useImportPreviewQuery,
  useImportProductsMutation,
} from '../api/baselinkerImportService'
import type { BaselinkerImportResponse } from '../schemas/baselinkerImportSchema'
import { AddFromBaselinkerSelectionActionBar } from '../components/AddFromBaselinkerSelectionActionBar'
import { BaselinkerProductsTable } from '../components/BaselinkerProductsTable'

export default function BaselinkerImportPage(): ReactElement {
  const { t } = useTranslation('features-products')
  const navigate = useNavigate()
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()

  // State
  const [selectedInventoryId, setSelectedInventoryId] = useState<number | null>(null)
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set())
  const [selectedVariantSkus, setSelectedVariantSkus] = useState<Set<string>>(new Set())
  const [importResult, setImportResult] = useState<BaselinkerImportResponse | null>(null)
  const [step, setStep] = useState<number>(0)

  // Queries
  const integrationsQuery = useIntegrationsStatus()
  const baselinkerStatus = integrationsQuery.data?.find(i => i.provider === 'BASELINKER')
  const isBaselinkerActive = baselinkerStatus?.status === 'ACTIVE'

  const currencyQuery = useQuery({
    queryKey: ['baselinker', 'currency'],
    queryFn: getBaselinkerCurrency,
    enabled: !!isBaselinkerActive,
    staleTime: 5 * 60 * 1000,
  })
  const isCurrencyConfigured = Boolean(currencyQuery.data?.currency)

  const orgSettingsQuery = useQuery({
    queryKey: activeOrgId ? ['org', activeOrgId, 'settings'] : ['settings', 'no-org'],
    queryFn: () => getOrgSettings(OrgIdSchema.parse(activeOrgId)),
    enabled: !!isBaselinkerActive && !!activeOrgId,
    staleTime: 5 * 60 * 1000,
  })

  // Determine if Step 0 is complete (or skipped)
  const isStep0Complete = isBaselinkerActive && isCurrencyConfigured

  // Derive effective step: auto-advance from 0 to 1 when prerequisites complete
  // This avoids cascading renders from setState in useEffect
  const effectiveStep = isStep0Complete && step === 0 ? 1 : step

  // Esc to go back (only when no selection active - selection bar handles its own Esc)
  const hasAnySelection = selectedProductIds.size > 0 || selectedVariantSkus.size > 0
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !hasAnySelection) {
        event.preventDefault()
        navigate('/dashboard/management/products')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate, hasAnySelection])

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-screen-lg mx-auto px-4 lg:px-8 pt-24 pb-8 space-y-8">
        <ScrollReveal blur>
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard/management/products')}
              className="gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              <Kbd>Esc</Kbd>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{t('import.title', 'Add from Baselinker')}</h1>
              <p className="text-muted-foreground">{t('import.subtitle', 'Add products and variants from your Baselinker catalog')}</p>
            </div>
          </div>
        </ScrollReveal>

        <div className="flex flex-col gap-8 pb-8">
          {/* Step 0: Prerequisites (Only shown if needed) */}
          {!isStep0Complete && (
            <ScrollReveal delay={0.1}>
              <Step0Prerequisites
                isBaselinkerActive={isBaselinkerActive}
                isCurrencyConfigured={isCurrencyConfigured}
                integrationsQuery={integrationsQuery}
                orgSettingsQuery={orgSettingsQuery}
                queryClient={queryClient}
              />
            </ScrollReveal>
          )}

          {/* Step 1: Select Catalog */}
          {(isStep0Complete || effectiveStep >= 1) && (
            <ScrollReveal delay={0.2}>
              <Step1Catalog
                isActive={effectiveStep === 1}
                isCompleted={effectiveStep > 1}
                selectedInventoryId={selectedInventoryId}
                onSelect={(id) => {
                  setSelectedInventoryId(id)
                  setStep(2)
                }}
              />
            </ScrollReveal>
          )}

          {/* Step 2: Preview & Select (hidden when Step 3 is shown) */}
          {effectiveStep === 2 && selectedInventoryId && (
            <ScrollReveal delay={0.3}>
              <Step2Preview
                isActive={effectiveStep === 2}
                isCompleted={effectiveStep > 2}
                inventoryId={selectedInventoryId}
                currency={currencyQuery.data?.currency ?? null}
                selectedProductIds={selectedProductIds}
                selectedVariantSkus={selectedVariantSkus}
                onProductSelectionChange={setSelectedProductIds}
                onVariantSelectionChange={setSelectedVariantSkus}
                onImport={async (result) => {
                  setImportResult(result)
                  setStep(3)
                }}
              />
            </ScrollReveal>
          )}

          {/* Step 3: Summary */}
          {effectiveStep >= 3 && importResult && (
            <ScrollReveal delay={0.4}>
              <Step3Summary
                result={importResult}
                onClose={() => navigate('/dashboard/management/products')}
                onImportMore={() => {
                  setStep(1)
                  setSelectedInventoryId(null)
                  setSelectedProductIds(new Set())
                  setImportResult(null)
                }}
              />
            </ScrollReveal>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Subcomponents ---

function Step0Prerequisites({
  isBaselinkerActive,
  isCurrencyConfigured,
  integrationsQuery,
  orgSettingsQuery,
  queryClient,
}: {
  isBaselinkerActive: boolean
  isCurrencyConfigured: boolean
  integrationsQuery: UseQueryResult<IntegrationStatus[], Error>
  orgSettingsQuery: UseQueryResult<OrgSettingsResponse, Error>
  queryClient: QueryClient
}): ReactElement {
  const { t } = useTranslation('features-products')

  const currencyMutation = useMutation({
    mutationFn: setBaselinkerCurrency,
    onSuccess: async () => {
      toast.success(t('import.prereqs.currencySaved'))
      await queryClient.invalidateQueries({ queryKey: ['baselinker', 'currency'] })
    },
    onError: () => {
      toast.error(t('import.prereqs.currencyError'))
    },
  })

  return (
    <section className="flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-secondary-foreground text-sm font-bold">0</div>
          {t('import.steps.prerequisites', 'Setup')}
        </h3>
        <p className="text-sm text-muted-foreground ml-10">{t('import.steps.prerequisitesDesc', 'Connect Baselinker and configure currency')}</p>
      </div>
      <div className="space-y-6 ml-10">
        {integrationsQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : !isBaselinkerActive ? (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <AlertCircle className="size-12 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{t('import.prereqs.notConnected')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('import.prereqs.notConnectedDescription')}
              </p>
            </div>
            <BaselinkerCredentialsPopover
              onSaved={async () => {
                await queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] })
              }}
            />
          </div>
        ) : !isCurrencyConfigured ? (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <Coins className="size-12 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">{t('import.prereqs.currencyNotSet')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('import.prereqs.currencyNotSetDescription')}
              </p>
            </div>
            <Select
              value=""
              onValueChange={(value) => currencyMutation.mutate(value)}
              disabled={currencyMutation.isPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('import.prereqs.selectCurrency')} />
              </SelectTrigger>
              <SelectContent>
                {(orgSettingsQuery.data?.allowedReportingCurrencies ?? []).map((code: string) => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function Step1Catalog({
  isActive,
  isCompleted,
  selectedInventoryId,
  onSelect,
}: {
  isActive: boolean
  isCompleted: boolean
  selectedInventoryId: number | null
  onSelect: (id: number) => void
}): ReactElement {
  const { t } = useTranslation('features-products')
  const inventoriesQuery = useBaselinkerInventoriesQuery(true)

  // Auto-select first catalog when data loads
  useEffect(() => {
    const firstInventory = inventoriesQuery.data?.[0]
    if (firstInventory && !selectedInventoryId && isActive) {
      onSelect(firstInventory.inventoryId)
    }
  }, [inventoriesQuery.data, selectedInventoryId, isActive, onSelect])

  return (
    <section className={`flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 transition-all duration-300 ${isActive ? 'ring-2 ring-primary/20' : isCompleted ? '' : 'opacity-60'}`}>
      <div className="mb-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'}`}>
            {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '1'}
          </div>
          {t('import.steps.catalog', 'Select Catalog')}
        </h3>
      </div>
      <div className="ml-10">
        {inventoriesQuery.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : inventoriesQuery.isError ? (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {t('import.step1.error')}
          </div>
        ) : (
          <Select
            value={selectedInventoryId?.toString() ?? ''}
            onValueChange={(val) => onSelect(parseInt(val, 10))}
            disabled={!isActive && !isCompleted}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder={t('import.step1.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {inventoriesQuery.data?.map(inventory => (
                <SelectItem key={inventory.inventoryId} value={inventory.inventoryId.toString()}>
                  {inventory.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </section>
  )
}

function Step2Preview({
  isActive,
  isCompleted,
  inventoryId,
  currency,
  selectedProductIds,
  selectedVariantSkus,
  onProductSelectionChange,
  onVariantSelectionChange,
  onImport,
}: {
  isActive: boolean
  isCompleted: boolean
  inventoryId: number
  currency: string | null
  selectedProductIds: Set<string>
  selectedVariantSkus: Set<string>
  onProductSelectionChange: (ids: Set<string>) => void
  onVariantSelectionChange: (skus: Set<string>) => void
  onImport: (result: BaselinkerImportResponse) => void
}): ReactElement {
  const { t } = useTranslation('features-products')
  const previewQuery = useImportPreviewQuery(inventoryId, true)
  const importMutation = useImportProductsMutation()
  const [searchTerm, setSearchTerm] = useState('')

  const allProducts = useMemo(() => {
    if (!previewQuery.data) return []
    return [
      ...previewQuery.data.newProducts,
      ...previewQuery.data.alreadyImportedProducts,
    ]
  }, [previewQuery.data])

  const handleSelectAllNew = (): void => {
    const newProductIds = new Set<string>()
    const newVariantSkus = new Set<string>()

    allProducts.forEach(p => {
      const importableVariants = p.variants?.filter(v => !v.alreadyExists) ?? []
      const hasVariants = (p.variants?.length ?? 0) > 0

      if (!hasVariants && !p.skuAlreadyExists) {
        newProductIds.add(p.baselinkerProductId)
      } else if (importableVariants.length === p.variants?.length) {
        newProductIds.add(p.baselinkerProductId)
      } else if (importableVariants.length > 0) {
        importableVariants.forEach(v => newVariantSkus.add(v.sku))
      }
    })

    onProductSelectionChange(newProductIds)
    onVariantSelectionChange(newVariantSkus)
  }

  const handleClearSelection = (): void => {
    onProductSelectionChange(new Set())
    onVariantSelectionChange(new Set())
  }

  const handleImportClick = async (): Promise<void> => {
    try {
      const result = await importMutation.mutateAsync({
        inventoryId,
        productIds: selectedProductIds.size > 0 ? Array.from(selectedProductIds) : undefined,
        variantSkus: selectedVariantSkus.size > 0 ? Array.from(selectedVariantSkus) : undefined,
      })
      onImport(result)
    } catch {
      toast.error(t('import.error'))
    }
  }

  return (
    <section className={`flex flex-col rounded-3xl border border-border/15 bg-card pb-6 pt-6 transition-all duration-300 ${isActive ? 'ring-2 ring-primary/20' : 'opacity-60'}`}>
      <div className="mb-6 grid grid-cols-[1fr_2fr_1fr] items-stretch gap-4 px-6">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${isCompleted ? 'text-positive' : 'bg-secondary text-secondary-foreground'}`}>
              {isCompleted ? <CheckCircle2 className="h-5 w-5" /> : '2'}
            </div>
            {t('import.steps.preview', 'Select Products')}
          </h3>
          <p className="text-sm text-muted-foreground ml-10">{t('import.steps.previewDesc')}</p>
        </div>
        <div className="relative w-full max-w-md flex items-center">
          <Search className="absolute left-2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('import.searchPlaceholder', 'Search products...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-full focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleSelectAllNew}
            disabled={!isActive}
            className="h-full"
          >
            {t('import.actions.selectAllNew', 'Select all new')}
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col min-h-[400px]">
        <BaselinkerProductsTable
          products={allProducts}
          currency={currency}
          selectedProductIds={selectedProductIds}
          selectedVariantSkus={selectedVariantSkus}
          onProductSelectionChange={onProductSelectionChange}
          onVariantSelectionChange={onVariantSelectionChange}
          isActive={isActive}
          isLoading={previewQuery.isLoading}
          isError={previewQuery.isError}
          showCostColumn
          showStatusColumn
          enableVariantSelection
          showExistingToggle
          searchTerm={searchTerm}
          noBorder
          maxHeight="none"
          className="flex-1"
          {...(previewQuery.data?.totalProducts !== undefined && { totalProducts: previewQuery.data.totalProducts })}
          {...(previewQuery.data?.alreadyImported !== undefined && { alreadyImported: previewQuery.data.alreadyImported })}
        />
      </div>

      <AddFromBaselinkerSelectionActionBar
        productCount={selectedProductIds.size}
        variantCount={selectedVariantSkus.size}
        isImporting={importMutation.isPending}
        onClearSelection={handleClearSelection}
        onImport={handleImportClick}
      />
    </section>
  )
}

function Step3Summary({
  result,
  onClose,
  onImportMore,
}: {
  result: BaselinkerImportResponse
  onClose: () => void
  onImportMore: () => void
}): ReactElement {
  const { t } = useTranslation('features-products')

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return (
    <section className="flex flex-col rounded-3xl border border-border/15 bg-card px-6 pb-6 pt-6 ring-2 ring-positive/20">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full text-positive text-sm font-bold">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          {t('import.steps.summary', 'Import Complete')}
        </h3>
        <p className="text-sm text-muted-foreground ml-10">{t('import.steps.summaryDesc', 'Import results summary')}</p>
      </div>
      <div className="space-y-6 ml-10">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-muted/50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-primary">{result.newlyImported}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('import.summary.imported')}</div>
          </div>
          <div className="bg-muted/50 p-4 rounded-lg text-center">
            <div className="text-2xl font-bold text-muted-foreground">{result.alreadyImported}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider">{t('import.summary.skipped')}</div>
          </div>
        </div>

        {result.failed > 0 && (
          <div className="bg-destructive/10 p-4 rounded-lg border border-destructive/20">
            <div className="flex items-center gap-2 text-destructive font-medium mb-2">
              <AlertCircle className="h-4 w-4" />
              {t('import.summary.failed', { count: result.failed })}
            </div>
            <ul className="text-sm text-destructive/80 list-disc list-inside">
              {result.errors.slice(0, 3).map((err, i) => (
                <li key={i}>{err.productName}: {err.errorMessage}</li>
              ))}
              {result.errors.length > 3 && (
                <li>...and {result.errors.length - 3} more</li>
              )}
            </ul>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-6 ml-10">
        <Button variant="ghost" onClick={onImportMore}>
          {t('import.actions.importMore', 'Import More')}
        </Button>
        <Button onClick={onClose}>
          {t('import.actions.done', 'Done')}
          <kbd className="ml-2 pointer-events-none h-5 select-none items-center gap-1 rounded border border-border/40 bg-muted px-1.5 font-mono text-[10px] font-medium opacity-60">
            Enter
          </kbd>
        </Button>
      </div>
    </section>
  )
}
